import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    // DEPRECATED
    getVehicleLastLocation,
    getVehicleTrackHistory,
    getVehicleStreamTicket,
    getVehicleStreamUrl,
    // Manifest
    getManifestLastLocation,
    getManifestTrackHistory,
    getManifestStreamTicket,
    getManifestStreamUrl,
    getManifestPlannedRoute,
    // Monitoring
    getActiveManifests,
    getMonitoringStreamTicket,
    getMonitoringStreamUrl,
    getManifestLiveRoute,
    getManifestLiveRouteHistory,
    getManifestLiveRouteStats,
    getManifestLiveRouteEta,
    getManifestMatchedTrack,
    getManifestInfo,
} from "../../api/gps/gpsTrackingApi";

/* ========================================================================== */
/* SSE base reutilizable                                                      */
/* ========================================================================== */
const useSseStream = ({ enabled, getTicket, getUrl, onMessage, listeners, deps = [] }) => {
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState(null);

    const esRef = useRef(null);
    const retryTimerRef = useRef(null);
    const retryCountRef = useRef(0);
    const onMessageRef = useRef(onMessage);
    const listenersRef = useRef(listeners || {});

    useEffect(() => { listenersRef.current = listeners || {}; }, [listeners]);
    useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

    const closeStream = useCallback(() => {
        if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
        if (esRef.current) { try { esRef.current.close(); } catch { /* noop */ } esRef.current = null; }
    }, []);

    useEffect(() => {
        if (!enabled) { closeStream(); setStatus("idle"); setError(null); return undefined; }
        let alive = true;

        const connect = async () => {
            setError(null);
            setStatus("ticket");
            try {
                const ticketPayload = await getTicket();
                if (!alive) return;
                const ticket = ticketPayload?.ticket;
                if (!ticket) throw new Error("Ticket vacío");

                const url = getUrl(ticket);
                setStatus("connecting");

                const es = new EventSource(url);
                esRef.current = es;

                es.onopen = () => { retryCountRef.current = 0; if (alive) setStatus("open"); };

                es.addEventListener("location", (ev) => {
                    const fn = listenersRef.current.location || onMessageRef.current;
                    if (fn) try { fn(JSON.parse(ev.data)); } catch { /* noop */ }
                });
                es.addEventListener("route_recalculated", (ev) => {
                    const fn = listenersRef.current.route_recalculated;
                    if (fn) try { fn(JSON.parse(ev.data)); } catch { /* noop */ }
                });
                es.addEventListener("ready", () => { /* noop */ });
                es.onmessage = (ev) => { if (onMessageRef.current) onMessageRef.current(ev.data); };

                es.onerror = () => {
                    if (!alive) return;
                    setStatus("error");
                    setError("Stream interrumpido. Reintentando...");
                    closeStream();
                    const attempt = retryCountRef.current++;
                    retryTimerRef.current = setTimeout(() => { if (alive) connect(); }, Math.min(1000 * (attempt + 1), 8000));
                };
            } catch (e) {
                if (!alive) return;
                setStatus("error");
                setError(e?.response?.data?.message || e?.message || "Error creando stream");
                const attempt = retryCountRef.current++;
                retryTimerRef.current = setTimeout(() => { if (alive) connect(); }, Math.min(1000 * (attempt + 1), 8000));
            }
        };

        connect();
        return () => { alive = false; closeStream(); setStatus("closed"); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, closeStream, ...deps]);

    return { status, error };
};

/* ========================================================================== */
/* DEPRECATED — vehID-first                                                   */
/* ========================================================================== */
/** @deprecated use useManifestLastLocation */
export const useVehicleLastLocation = (vehid, manid, enabled = true) =>
    useQuery({
        queryKey: ["gps-last-location", vehid, manid],
        queryFn: () => getVehicleLastLocation({ vehid, manid }),
        enabled: !!vehid && !!manid && enabled,
        refetchInterval: 2000,
        refetchIntervalInBackground: true,
        staleTime: 0,
    });

/** @deprecated use useManifestTrackHistory */
export const useVehicleTrackHistory = (vehid, manid, enabled = true) =>
    useQuery({
        queryKey: ["gps-history", vehid, manid],
        queryFn: () => getVehicleTrackHistory({ vehid, manid }),
        enabled: !!vehid && !!manid && enabled,
        staleTime: 30_000,
    });

/** @deprecated use useManifestLocationStream */
export const useVehicleLocationStream = ({ vehid, manid, enabled }) => {
    const [lastLoc, setLastLoc] = useState(null);
    const handle = useCallback((payload) => {
        if (payload && typeof payload === "object" && "lat" in payload) setLastLoc(payload);
    }, []);
    const { status, error } = useSseStream({
        enabled: !!enabled && !!vehid && !!manid,
        getTicket: () => getVehicleStreamTicket({ vehid, manid }),
        getUrl: (ticket) => getVehicleStreamUrl({ vehid, manid, ticket }),
        onMessage: handle,
        deps: [vehid, manid],
    });
    return { lastLoc, status, error };
};

/* ========================================================================== */
/* Manifest                                                                    */
/* ========================================================================== */
export const useManifestLastLocation = (manid, enabled = true) =>
    useQuery({
        queryKey: ["gps-manifest-last", manid],
        queryFn: () => getManifestLastLocation(manid),
        enabled: !!manid && enabled,
        refetchInterval: 5000,
        refetchIntervalInBackground: false,
        staleTime: 0,
    });

export const useManifestTrackHistory = (manid, { enabled = true, refetchInterval = false } = {}) =>
    useQuery({
        queryKey: ["gps-manifest-history", manid],
        queryFn: () => getManifestTrackHistory(manid),
        enabled: !!manid && enabled,
        refetchInterval,
        staleTime: 30_000,
    });

export const useManifestPlannedRoute = (manid, enabled = true) =>
    useQuery({
        queryKey: ["gps-manifest-planned-route", manid],
        queryFn: () => getManifestPlannedRoute(manid),
        enabled: !!manid && enabled,
        staleTime: 5 * 60 * 1000,
        retry: (count, err) => err?.response?.status !== 404 && count < 2,
    });

export const useManifestLocationStream = ({ manid, enabled }) => {
    const [lastLoc, setLastLoc] = useState(null);
    const [lastRouteEvent, setLastRouteEvent] = useState(null);

    const handleLocation = useCallback((payload) => {
        if (payload && typeof payload === "object" && "lat" in payload) setLastLoc(payload);
    }, []);
    const handleRouteEvent = useCallback((payload) => {
        if (payload && payload.type === "route_recalculated") setLastRouteEvent(payload);
    }, []);

    const { status, error } = useSseStream({
        enabled: !!enabled && !!manid,
        getTicket: () => getManifestStreamTicket(manid),
        getUrl: (ticket) => getManifestStreamUrl({ manid, ticket }),
        listeners: { location: handleLocation, route_recalculated: handleRouteEvent },
        deps: [manid],
    });
    return { lastLoc, lastRouteEvent, status, error };
};

/* ========================================================================== */
/* Sala de monitoreo                                                          */
/* ========================================================================== */
export const useActiveManifests = ({ enabled = true, refetchInterval = 30_000 } = {}) =>
    useQuery({
        queryKey: ["gps-monitoring-active"],
        queryFn: getActiveManifests,
        enabled,
        refetchInterval,
        staleTime: 10_000,
    });

export const useMonitoringStream = ({ enabled }) => {
    const [byManId, setByManId] = useState({});
    const handle = useCallback((payload) => {
        if (!payload || typeof payload !== "object") return;
        const key = payload.manid ?? payload.manId;
        if (key == null) return;
        setByManId((prev) => ({ ...prev, [key]: payload }));
    }, []);
    const { status, error } = useSseStream({
        enabled: !!enabled,
        getTicket: getMonitoringStreamTicket,
        getUrl: (ticket) => getMonitoringStreamUrl({ ticket }),
        onMessage: handle,
        deps: [],
    });
    return { byManId, status, error };
};

/* ========================================================================== */
/* Ruta viva / ETA / matched-track                                            */
/* ========================================================================== */
export const useManifestLiveRoute = (manid, { enabled = true, staleTime = 5_000 } = {}) =>
    useQuery({
        queryKey: ["gps-manifest-live-route", manid],
        queryFn: () => getManifestLiveRoute(manid),
        enabled: !!manid && enabled,
        staleTime,
        retry: (count, err) => err?.response?.status !== 404 && count < 2,
    });

export const useManifestLiveRouteHistory = (manid, { enabled = true, limit = 100 } = {}) =>
    useQuery({
        queryKey: ["gps-manifest-live-route-history", manid, limit],
        queryFn: () => getManifestLiveRouteHistory(manid, { limit }),
        enabled: !!manid && enabled,
        staleTime: 30_000,
    });

export const useManifestLiveRouteStats = (manid, { enabled = true } = {}) =>
    useQuery({
        queryKey: ["gps-manifest-live-route-stats", manid],
        queryFn: () => getManifestLiveRouteStats(manid),
        enabled: !!manid && enabled,
        staleTime: 10_000,
        refetchInterval: 30_000,
        retry: (count, err) => err?.response?.status !== 404 && count < 2,
    });

/**
 * ETA ligera. Polling cada 15s (backend cachea 30s → 50% hits de cache).
 * No usa retry en 404 (manifiesto sin ruta viva todavía).
 */
export const useManifestLiveRouteEta = (
    manid,
    { enabled = true, refetchInterval = 15_000 } = {}
) =>
    useQuery({
        queryKey: ["gps-manifest-live-route-eta", manid],
        queryFn: () => getManifestLiveRouteEta(manid),
        enabled: !!manid && enabled,
        staleTime: 10_000,
        refetchInterval,
        retry: (count, err) => err?.response?.status !== 404 && count < 2,
    });

/**
 * Track real (map-matched).
 * @param {number} manid
 * @param {object} opts
 *   format: 'feature' (default v7) | 'simple' (v6 compat)
 *   limit : máx puntos crudos a procesar
 */
export const useManifestMatchedTrack = (
    manid,
    { enabled = true, refetchInterval = false, format = "feature", limit } = {}
) =>
    useQuery({
        queryKey: ["gps-manifest-matched-track", manid, format, limit ?? null],
        queryFn: () => getManifestMatchedTrack(manid, { format, limit }),
        enabled: !!manid && enabled,
        staleTime: 60_000,
        refetchInterval,
        retry: (count, err) => err?.response?.status !== 404 && count < 2,
    });

/* ========================================================================== */
/* Helpers de velocidad (alineados con speedClassifyService del backend)      */
/* ========================================================================== */
export const SPEED_CLASSES = {
    stop: { color: '#9CA3AF', label: 'Detenido', bg: '#f3f4f6' },
    slow: { color: '#60A5FA', label: 'Lento', bg: '#eff6ff' },
    normal: { color: '#22C55E', label: 'Normal', bg: '#f0fdf4' },
    fast: { color: '#F59E0B', label: 'Rápido', bg: '#fffbeb' },
    speeding: { color: '#EF4444', label: 'Exceso', bg: '#fef2f2' },
    critical: { color: '#7F1D1D', label: 'Crítico', bg: '#fee2e2' },
};

export const classifySpeed = (kmh) => {
    if (!Number.isFinite(kmh)) return null;
    if (kmh < 3) return { class: 'stop', ...SPEED_CLASSES.stop, kmh };
    if (kmh < 30) return { class: 'slow', ...SPEED_CLASSES.slow, kmh };
    if (kmh < 70) return { class: 'normal', ...SPEED_CLASSES.normal, kmh };
    if (kmh < 90) return { class: 'fast', ...SPEED_CLASSES.fast, kmh };
    if (kmh < 120) return { class: 'speeding', ...SPEED_CLASSES.speeding, kmh };
    return { class: 'critical', ...SPEED_CLASSES.critical, kmh };
};

export const isSpeeding = (m, thresholdKmh = 90) => {
    const speed = m?.lastLocation?.speedKmh;
    return Number.isFinite(speed) && speed >= thresholdKmh;
};

export const deriveManifestStatus = (m) => {
    if (!m) return { key: 'unknown', label: 'Desconocido', color: 'default', priority: 99 };
    if (m.isStale) return { key: 'stale', label: 'Sin señal', color: 'warning', priority: 1 };
    if (isSpeeding(m)) return { key: 'speeding', label: 'Exceso', color: 'error', priority: 2 };
    if (!m.lastLocation) return { key: 'noloc', label: 'Sin GPS', color: 'default', priority: 3 };
    if (m.sessionStatus === 'ACTIVE') return { key: 'active', label: 'En ruta', color: 'success', priority: 4 };
    return { key: 'other', label: m.sessionStatus || '—', color: 'default', priority: 9 };
};

export const computeHealth = (m, now = Date.now()) => {
    const hb = m.lastHeartbeatServer || m.lastHeartbeatClient;
    const hbAgeSec = hb ? (now - new Date(hb).getTime()) / 1000 : null;
    const gpsAgeSec = m.lastLocation?.timestamp
        ? (now - new Date(m.lastLocation.timestamp).getTime()) / 1000
        : null;
    return {
        hbAgeSec, gpsAgeSec,
        hbFresh: hbAgeSec != null && hbAgeSec < 90,
        gpsFresh: gpsAgeSec != null && gpsAgeSec < 90,
    };
};

export const useManifestInfo = (manid, { enabled = true, refetchInterval = false } = {}) =>
    useQuery({
        queryKey: ["gps-manifest-info", manid],
        queryFn: () => getManifestInfo(manid),
        enabled: !!manid && enabled,
        refetchInterval,
        staleTime: 30_000,
        retry: (count, err) => err?.response?.status !== 404 && count < 2,
    });

export const pickDriver = (info) => {
    if (!info) return null;
    if (info.hasActiveDriver && info.empId) {
        return {
            empId: info.empId,
            nombre: info.empNombre,
            nombreCorto: info.empNombreCorto,
            rol: info.rol,
            isActive: true,
            startedAt: info.startedAt,
        };
    }
    // Fallback: último DRIVER del historial
    if (info.lastDriver) {
        return {
            empId: info.lastDriver.empId,
            nombre: info.lastDriver.nombreCompleto,
            nombreCorto: info.lastDriver.nombreCorto,
            rol: 'DRIVER',
            isActive: false,
            startedAt: info.lastDriver.startAt,
            endAt: info.lastDriver.endAt,
        };
    }
    return null;
};